<script setup lang="ts">
import { useDeviceState } from "@/stores/DeviceState";
import { computed, ref } from "vue";
import { useArchiveManyDrawings, useMoveManyDrawings } from "@/stores/queries";
import DRResponsiveBtn from "../../UI/DRResponsiveBtn.vue";
import SearchProjectDialog from "../../projects/SearchProjectDialog.vue";
import DRSelector from "@/components/UI/DRSelector.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";

const props = defineProps<{
  selectMode: boolean;
  selectedIds: number[];
  drawingIds: number[];
  projectId: number;
  adminMode: boolean;
  currentUserId?: number;
}>();

const emits = defineEmits<{
  (e: "select", values: number[]): void;
  (e: "update:selectMode", value: boolean): void;
}>();

const deviceState = useDeviceState();
const { mutateAsync: archiveMutation, isPending: isArchiving } =
  useArchiveManyDrawings();
const { mutateAsync: moveMutation, isPending: isMoving } =
  useMoveManyDrawings();

const showConfirmArchive = ref(false);
async function archiveSelection() {
  archiveMutation(props.selectedIds, {
    onSuccess: () => {
      emits("select", []);
      showConfirmArchive.value = false;
    },
  });
}
const showMoveDialog = ref(false);
const showTransferDialog = ref(false);

async function moveSelection(newProjectId: number) {
  moveMutation(
    { ids: props.selectedIds, projectId: newProjectId },
    {
      onSuccess: () => {
        emits("select", []);
        showMoveDialog.value = false;
      },
    }
  );
}

const drawingsCount = computed(() => {
  return props.drawingIds.length;
});

const handleSelectAllChange = (
  value: boolean | undefined | string | number
) => {
  emits("select", value ? props.drawingIds : []);
};

const mode = computed({
  get: () => props.selectMode,
  set: (value: boolean) => {
    emits("update:selectMode", value);
  },
});
</script>

<template>
  <div
    v-if="drawingsCount"
    class="d-flex align-items-center gap-3 shadow-box rounded justify-content-between"
  >
    <DRSelector
      :selectedIds="selectedIds"
      :itemsIds="drawingIds"
      v-model:selectMode="mode"
      @selectAll="handleSelectAllChange"
    >
      <template #actions>
        <DRResponsiveBtn
          v-if="adminMode"
          variant="flat"
          :disabled="!deviceState.isOnline"
          icon="bi-person-up"
          @click="showTransferDialog = true"
          :label="$t('dashboard:drawings.transfer.btn')"
        />

        <div v-else class="d-flex align-items-center gap-2">
          <DRResponsiveBtn
            variant="flat"
            :disabled="!deviceState.isOnline"
            icon="bi-archive"
            @click="showConfirmArchive = true"
            :label="$t('archive')"
          />
          <DRResponsiveBtn
            variant="flat"
            :disabled="!deviceState.isOnline"
            icon="bi-folder-symlink"
            @click="showMoveDialog = true"
            :label="$t('move')"
          />
        </div>
      </template>
    </DRSelector>

    <SearchProjectDialog
      v-model="showMoveDialog"
      :title="$t('dashboard:move.to.3dots')"
      :disabledProject="projectId"
      :loading="isMoving"
      @select="moveSelection($event)"
    />

    <ConfirmationModal
      v-model="showConfirmArchive"
      :title="$t('dashboard:drawings.archive.confirmation.title')"
      :message="$t('dashboard:drawings.archive.confirmation.message')"
      :loading="isArchiving"
      @confirm="archiveSelection"
    />
  </div>
</template>
